import Axios, {
	AxiosResponse,
	AxiosRequestConfig,
	AxiosError,
	CancelTokenSource,
	AxiosInstance,
	AxiosPromise,
} from 'axios';
import { cacheAdapterEnhancer, ICacheLike } from 'axios-extensions';
import { sleep } from '@/helpers/async-helper';
import LRUCache from 'lru-cache';
import http from 'http';
import https from 'https';
import UserHelper from '@/helpers/user-helper';
let axios: any = null;
let axiosCached: any = null;

// #### DEBUG API CALLS
// Axios.interceptors.request.use(config => {
// 	console.log('[API] url: ', config.url);
// 	console.log('[API] config: ', config);
// 	return config;
// });
// #### DEBUG API CALLS

function createAxios(options: any = {}) {
	return Axios.create({
		baseURL: '/',
		timeout: 20000,

		//keepAlive pools and reuses TCP connections, so it's faster
		httpAgent: new http.Agent({ keepAlive: true }),
		httpsAgent: new https.Agent({ keepAlive: true }),
		...options,
	});
}

export function getAxios(forceInit: boolean = false): AxiosInstance {
	if (!axios || forceInit) {
		if (process.server && global.DEBUG_AXIOS === 'true') {
			require('axios-debug-log/enable');
		}
		axios = null;
		axios = createAxios();
	}
	return axios;
}

export function getAxiosCached(): AxiosInstance {
	if (!axiosCached) {
		if (process.server && global.DEBUG_AXIOS === 'true') {
			console.debug('[axios] Debug Enabled');
			require('axios-debug-log/enable');
		}
		// cache doesn't add any value on client. browsers are taking care of request caching already.
		axiosCached = process.server ? createAxios(withCache()) : getAxios();
	}

	return axiosCached;
}

function withCache(options: any = {}) {
	const ONE_HOUR = 60 * 60 * 1000;
	// const SIX_HOUR = 6 * ONE_HOUR;
	// const TWELVE_HOUR = 12 * ONE_HOUR;

	let ttl = ONE_HOUR;
	if (process.env.VUE_APP_AXIOS_CACHE_DURATION_MINS) {
		ttl = Number.parseInt(process.env.VUE_APP_AXIOS_CACHE_DURATION_MINS) * 60 * 1000;
	}

	if (process.env.VUE_APP_AXIOS_CACHE_DURATION_HOURS) {
		ttl = Number.parseInt(process.env.VUE_APP_AXIOS_CACHE_DURATION_HOURS) * ONE_HOUR;
	}

	let max = 12000; // max entries for the LRU cache
	if (process.env.VUE_APP_AXIOS_CACHE_MAX_ENTRIES) {
		max = Number.parseInt(process.env.VUE_APP_AXIOS_CACHE_MAX_ENTRIES);
	}

	options.headers = {
		...options.headers,
		'Cache-Control': 'no-cache',
	};
	options.adapter = cacheAdapterEnhancer(Axios.defaults.adapter!, {
		defaultCache: new LRUCache({ ttl, max }) as unknown as ICacheLike<AxiosPromise>,
	});
	return options;
}

export function addServerCustomHeaders(customHeaders: any): AxiosInstance {
	Axios.defaults.headers = {
		...Axios.defaults.headers,
		...customHeaders,
	};

	return axios;
}

export function setAxiosUserContentInterceptors(axios: AxiosInstance, store: any) {
	let _axios = addAccessTokenInterceptor(axios, store);
	if (process.client) {
		_axios = addLoggedOutInterceptor(_axios, store);
	}
	return _axios;
}

export function setAxiosInterceptors(axios: AxiosInstance) {
	return addEnforceTextPlainInterceptor(axios);
}

/**
 * execute a API request with Axios cancel token and return an array with
 * [promise, cancelTokenSource]
 *
 * the promise returns an array with the response and a boolean value that states
 * if the request has been canceled
 * [response, isCanceled]
 *
 * @param {CancelTokenSource} cancelToken
 * @param {Promise<any>} request
 *
 * @throws {AxiosError}
 *
 * @returns {[promise, cancelTokenSource]}
 */
export function cancellableRequest(cancelTokenSource: CancelTokenSource, request: Promise<any>) {
	const promise = new Promise((resolve, reject) =>
		request
			.then(resp => resolve([resp, false]))
			.catch(err => {
				if (Axios.isCancel(err)) {
					resolve([[], true]);
				} else {
					reject(err);
				}
			})
	);
	return [promise, cancelTokenSource];
}

/**
 * attempt `n` times a Axios **GET** request, with a specific `interval`
 *
 * @param {string} url
 * @param {AxiosRequestConfig} [config={}]
 * @param {number} n
 * @param {number} [interval=50]
 *
 * @throws {AxiosError}
 *
 * @returns {Promise<R>}
 */
export async function attemptAxiosGetRequest<T = any, R = AxiosResponse<T>>(
	url: string,
	config: AxiosRequestConfig = {},
	n: number,
	interval = 50
): Promise<R> {
	try {
		return await getAxios().get<T, R>(url, config);
	} catch (err) {
		if (n <= 1) throw <AxiosError>err;

		await sleep(interval);
		return attemptAxiosGetRequest<T, R>(url, config, n - 1, interval);
	}
}

/**
 * attempt `n` times a Axios **POST** request, with a specific `interval`
 *
 * @param {string} url
 * @param {any} [data=undefined]
 * @param {AxiosRequestConfig} [config={}]
 * @param {number} n
 * @param {number} [interval=50]
 *
 * @throws {AxiosError}
 *
 * @returns {Promise<R>}
 */
export async function attemptAxiosPostRequest<T = any, R = AxiosResponse<T>>(
	url: string,
	data: any = undefined,
	config: AxiosRequestConfig = {},
	n: number,
	interval = 50
): Promise<R> {
	try {
		return await getAxios().post<T, R>(url, data, config);
	} catch (err) {
		if (n <= 1) throw <AxiosError>err;
		await sleep(interval);
		return attemptAxiosPostRequest<T, R>(url, data, config, n - 1, interval);
	}
}

/**
 * adds the access token to whitelisted requests if available.
 */
function addAccessTokenInterceptor(axios: AxiosInstance, store: any) {
	const authHeaderEndpoints = [
		'/discovery',
		'/personalization',
		'/store',
		'/tracking', // Todo is this still used?
		'/tv/connect',
	];
	Axios.interceptors.request.use(async (config: AxiosRequestConfig<any>) => {
		if (config.url && store.state.user.accessToken) {
			await store.dispatch('user/refreshToken');
			const url = new URL(config.url);
			const useAuthHeader = !!authHeaderEndpoints.filter(endpoint => url.pathname.startsWith(endpoint)).length;
			if (useAuthHeader) {
				// @ts-ignore
				config.headers.common['Authorization'] = 'Bearer ' + store.state.user.accessToken;
			}
		}
		return config;
	});

	return axios;
}

/**
 * enforces `text/plain` for post @see:https://stackoverflow.com/questions/12320467/jquery-cors-content-type-options/12320736#12320736
 */
export function addEnforceTextPlainInterceptor(axios: AxiosInstance): AxiosInstance {
	axios.interceptors.request.use((config: AxiosRequestConfig<any>) => {
		if (config.method && config.method.toLowerCase() === 'post') {
			// @ts-ignore
			config.headers['Content-Type'] = 'text/plain';
		}
		return config;
	});

	return axios;
}

export function addWithCredentialsInterceptor(axios: AxiosInstance): AxiosInstance {
	const credentialEnabledUrls = ['i.tw.cx', 'i.twcx.de'];
	axios.interceptors.request.use(config => {
		if (credentialEnabledUrls.filter(url => (config.url || '').indexOf(url) !== -1).length > 0) {
			config.withCredentials = true;
		}
		return config;
	});

	return axios;
}

export function addLoggedOutInterceptor(axios: AxiosInstance, store: any): AxiosInstance {
	axios.interceptors.response.use(
		response => response,
		async error => {
			if (error?.response?.status) {
				// check if it is a bot generating these errors
				if ([401, 403, 404, 405, 409].indexOf(error.response.status) !== -1 && store.state.app.isBot) {
					return Promise.resolve();
				}

				if (process.client) {
					// UNAUTZORIZED / FORBIDDEN
					if ([401, 403].indexOf(error.response.status) !== -1) {
						await store.dispatch('user/fetchJwId', undefined, { root: true });

						return Promise.reject('FORBIDDEN');
					}
					// MERGE USER ACCOUNTS
					if (error.response.status === 409) {
						store.dispatch('user/logout', undefined, { root: true });
						return Promise.reject('MERGE USER ACCOUNTS');
					}
				}
			}
			return Promise.reject(error);
		}
	);

	return axios;
}

/**
 * usually we'd use interceptors on axios to add auth headers on specific routes.
 * due to a CPU leak in axios interceptors, we now have to add headers manually on the routes.
 * @todo switch to interceptors again when axios fixed the CPU leak in interceptors (planned for 0.22.0)
 */
export async function getAuthHeaders(accessToken?: string, baseHeaders: any = {}) {
	if (accessToken) {
		if (process.server) {
			return {
				...baseHeaders,
				Authorization: `Bearer ${accessToken}`,
			};
		} else {
			const token = await UserHelper.generateToken();
			// Still fallback to the state access token for users that still use legacy token
			return {
				...baseHeaders,
				Authorization: `Bearer ${token || accessToken}`,
			};
		}
	}

	return { ...baseHeaders };
}
